import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import AddStore from "../store/AddStore";
import {
  extractStoreId,
  extractStoreName,
  fetchStoreById,
} from "../store/storeApi";
import EatsAdminLayout from "./EatsAdminLayout";
import EatsAdminCreateStepper from "./EatsAdminCreateStepper";
import EatsAdminPageHeader from "./EatsAdminPageHeader";
import RootAdminAssignPanel from "./RootAdminAssignPanel";
import "./EatsAdminCreatePage.css";

function toActiveStore(store, fallbackId = null) {
  const storeId = extractStoreId(store) || fallbackId;
  if (!storeId) return null;
  return {
    storeId,
    storeName: extractStoreName(store) || store?.storeName || "",
    shortCode: store?.shortCode || store?.Short_Code || "",
  };
}

const EatsAdminCreateStorePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  }, []);
  const currentUserId = currentUser?.userId || null;

  const [tab, setTab] = useState("company");
  const [activeStore, setActiveStore] = useState(null);

  useEffect(() => {
    const requestedId = searchParams.get("storeId");
    if (!requestedId) {
      return undefined;
    }

    let cancelled = false;
    const loadStore = async () => {
      try {
        const store = await fetchStoreById(requestedId);
        if (cancelled) return;
        const next = toActiveStore(store, requestedId);
        if (!next) {
          setActiveStore(null);
          setTab("company");
          return;
        }
        setActiveStore(next);
        setTab("root-admin");
      } catch (error) {
        console.error("Failed loading store:", error?.response?.data || error?.message);
        if (!cancelled) {
          setActiveStore(null);
          setTab("company");
        }
      }
    };
    loadStore();
    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  const handleStoreCreated = (result) => {
    const store = result?.store || result || {};
    const next = toActiveStore(store);
    if (!next) {
      alert("Store created but storeId was not returned. Please retry.");
      return;
    }
    setActiveStore(next);
    setTab("root-admin");
    navigate(`/eats/admin/stores/create?storeId=${next.storeId}`, { replace: true });
  };

  return (
    <EatsAdminLayout activeTab="stores">
      <div className="eats-admin-create-page">
        <EatsAdminPageHeader
          title="Create Store"
          subtitle="Create store first, then assign root admin user."
          backTo="/eats/admin?tab=stores"
        />

        <EatsAdminCreateStepper
          activeStepId={tab}
          onStepChange={(next) => {
            if (next === "root-admin" && !activeStore?.storeId) return;
            setTab(next);
          }}
          steps={[
            { id: "company", label: "Business" },
            {
              id: "root-admin",
              label: "Create Root Admin",
              disabled: !activeStore?.storeId,
            },
          ]}
        />

        <div className="eats-admin-panel">
          {tab === "company" ? (
            <AddStore
              embedded
              createdById={currentUserId}
              onSuccess={handleStoreCreated}
              onClose={() => {}}
            />
          ) : activeStore ? (
            <RootAdminAssignPanel
              entityType="store"
              entityId={activeStore.storeId}
              entityName={activeStore.storeName}
              createdById={currentUserId}
              title={`Create Owner for ${
                activeStore.storeName || `Store ${activeStore.storeId}`
              }`}
              onSuccess={() => navigate("/eats/admin?tab=stores")}
            />
          ) : (
            <div className="eats-admin-empty-state">Please create a store first.</div>
          )}
        </div>
      </div>
    </EatsAdminLayout>
  );
};

export default EatsAdminCreateStorePage;
